// src/layouts/components/dashboard/DashboardPinnedSection.jsx

import { Link, useNavigate } from "react-router-dom";

import CatalogEntryRow from "../../../catalog/components/CatalogEntryRow";
import useCatalogHome from "../../../catalog/hooks/useCatalogHome";
import { getCatalogLearnMoreDestination } from "../../../catalog/utils/catalogAccess";
import useCatalogEntryPersonalization from "../../../catalog/hooks/useCatalogEntryPersonalization";
import { openCatalogLaunch } from "../../../catalog/utils/catalogLaunch";
import AVIcon from "../../../components/icons/AllVoicedIcons";
import {
  ApplicationButton,
  ApplicationContextualHelp,
  ApplicationSectionHeader,
  ApplicationSurface
} from "../../../components/application";

export default function DashboardPinnedSection() {
  const navigate = useNavigate();

  const {
    catalogHome,
    loading,
    error
  } = useCatalogHome();
  const {
    pinnedIds,
    decorateEntriesWithViewerContext,
    handleToggleFavorite,
    handleTogglePinned,
  } = useCatalogEntryPersonalization(catalogHome);

  const pinnedEntries = decorateEntriesWithViewerContext(
    (catalogHome?.pinnedEntries || []).filter((entry) => {
      const entryId = entry?._id || entry?.id;

      return entryId
        ? pinnedIds.has(entryId.toString())
        : false;
    })
  );

  const handleLearnMore = (entry) => {
    const destination =
      getCatalogLearnMoreDestination(entry);

    if (destination?.target === "_blank") {
      window.open(destination.to, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(destination?.to || `/app/catalog/${entry.slug}`);
  };

  const handleLaunch = (entry) => {
    const launched = openCatalogLaunch(entry, navigate);

    if (!launched) {
      navigate(`/app/catalog/${entry.slug}`);
    }
  };

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  return (
    <section
      aria-labelledby="dashboard-pinned-title"
      className="av-dashboard-section av-dashboard-pinned"
    >
      {pinnedEntries.length > 0 ? (
        <>
          <ApplicationSectionHeader
            title={(
              <span className="av-app-section-header__title-row">
                <span>Pinned items</span>
                <ApplicationContextualHelp
                  label="About pinned items"
                  title="About pinned items"
                >
                  <p>
                    Pinned items give you quick access from your Dashboard to
                    {" "}
                    <Link to="/app/catalog">Catalog</Link> entries you use
                    often. Open the Catalog and pin the activities, programs,
                    resources, or other entries you want to keep within easy
                    reach.
                  </p>
                </ApplicationContextualHelp>
              </span>
            )}
            titleId="dashboard-pinned-title"
            description="Quick access to the catalog entries you have pinned to your dashboard."
            actions={
              <ApplicationButton
                variant="secondary"
                onClick={() => navigate("/app/catalog")}
              >
                Manage in catalog
              </ApplicationButton>
            }
          />

          <div className="av-dashboard-catalog-row">
            <CatalogEntryRow
              title="Pinned items"
              entries={pinnedEntries}
              onLearnMore={handleLearnMore}
              onLaunch={handleLaunch}
              onToggleFavorite={handleToggleFavorite}
              onTogglePinned={handleTogglePinned}
              layout="carousel"
            />
          </div>
        </>
      ) : (
        <>
          <ApplicationSectionHeader
            title={(
              <span className="av-app-section-header__title-row">
                <span>Pinned items</span>
                <ApplicationContextualHelp
                  label="About pinned items"
                  title="About pinned items"
                >
                  <p>
                    Pinned items give you quick access from your Dashboard to
                    {" "}
                    <Link to="/app/catalog">Catalog</Link> entries you use
                    often. Open the Catalog and pin the activities, programs,
                    resources, or other entries you want to keep within easy
                    reach.
                  </p>
                </ApplicationContextualHelp>
              </span>
            )}
            titleId="dashboard-pinned-title"
            description="Quick access to the catalog entries you have pinned to your dashboard."
          />
          <ApplicationSurface
            as="div"
            className="av-dashboard-empty"
            variant="subtle"
          >
            <div
              className="av-app-icon-badge av-dashboard-empty__icon"
              aria-hidden="true"
            >
              <AVIcon
                icon="pin1"
                size={24}
              />
            </div>

            <div>
              <h3>No pinned items yet</h3>

              <p>
                Pin catalog entries to keep your most useful programs,
                activities, services, or resources within quick
                reach from your dashboard.
              </p>

              <ApplicationButton
                onClick={() => navigate("/app/catalog")}
              >
                Open catalog
              </ApplicationButton>
            </div>
          </ApplicationSurface>
        </>
      )}
    </section>
  );
}
